import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RemoveIcon from '@mui/icons-material/Remove';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ConsoleApiError } from '@/api/client';
import * as permissionsApi from '@/api/permissions';
import * as usersApi from '@/api/users';
import type { SystemUserDetail } from '@/api/users';
import { useSnackbar } from '@/components/Snackbar';
import { act, ValueActions } from '@/components/ValueActions/ValueActions';
import { usePermissions } from '@/hooks/usePermissions';

interface Props {
  user: SystemUserDetail;
  onChanged: () => void;
}

export function PermissionsTab({ user, onChanged }: Props) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const { hasPerm } = usePermissions();
  const canEdit = hasPerm('console_perm_grant');

  const catalog = useQuery({
    queryKey: ['permissions', 'catalog'],
    queryFn: () => permissionsApi.list(),
  });

  const grant = useMutation({
    mutationFn: (codename: string) => usersApi.grantPermission(user.id, codename),
    onSuccess: () => {
      snackbar.show('Permission granted.', 'success');
      qc.invalidateQueries({ queryKey: ['consoleUsers', 'detail', user.id] });
      onChanged();
    },
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Grant failed.', 'error'),
  });
  const revoke = useMutation({
    mutationFn: (codename: string) => usersApi.revokePermission(user.id, codename),
    onSuccess: () => {
      snackbar.show('Permission revoked.', 'success');
      qc.invalidateQueries({ queryKey: ['consoleUsers', 'detail', user.id] });
      onChanged();
    },
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Revoke failed.', 'error'),
  });

  if (catalog.isLoading) return <CircularProgress />;
  if (catalog.isError) return <Alert severity="error">Couldn’t load permission catalog.</Alert>;

  const owned = new Set(user.permissions);

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
      <Box sx={{ flex: 1, minWidth: 320 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Effective permissions
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Effective = direct grants + group-derived. Revoke acts on direct grants only.
        </Typography>
        <List dense>
          {user.permissions.length === 0 && (
            <ListItem>
              <ListItemText secondary="No console permissions." />
            </ListItem>
          )}
          {user.permissions.map((codename) => (
            <ListItem
              key={codename}
              secondaryAction={
                canEdit ? (
                  <IconButton
                    edge="end"
                    aria-label="Revoke"
                    onClick={() => revoke.mutate(codename)}
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                ) : undefined
              }
            >
              <ListItemText
                primary={
                  <ValueActions actions={[act.copy(codename, 'Copy codename')]}>
                    {codename}
                  </ValueActions>
                }
                secondary={
                  catalog.data?.permissions.find((p) => p.codename === codename)?.name ?? ''
                }
              />
            </ListItem>
          ))}
        </List>
      </Box>

      <Divider orientation="vertical" flexItem />

      <Box sx={{ flex: 1, minWidth: 320 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Available to grant
        </Typography>
        {/* A read-only note, not an amber chip: amber means unsaved/pending.
            Above the list, so it is seen before the long catalog. */}
        {!canEdit && (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1, color: 'text.secondary' }}>
            <LockOutlinedIcon fontSize="small" />
            <Typography variant="body2">You don&apos;t have permission to grant or revoke.</Typography>
          </Stack>
        )}
        <List dense>
          {catalog.data?.permissions
            .filter((p) => !owned.has(p.codename))
            .map((p) => (
              <ListItem
                key={p.codename}
                secondaryAction={
                  canEdit ? (
                    <IconButton edge="end" onClick={() => grant.mutate(p.codename)}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  ) : undefined
                }
              >
                <ListItemText
                  primary={
                    <ValueActions actions={[act.copy(p.codename, 'Copy codename')]}>
                      {p.codename}
                    </ValueActions>
                  }
                  secondary={p.name}
                />
              </ListItem>
            ))}
        </List>
      </Box>
    </Stack>
  );
}
